import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PiCrownSimple,
  PiPencilSimple,
  PiPlus,
  PiUsersThree,
} from 'react-icons/pi';
import AccountPageHeader from '../components/AccountPageHeader';
import BottomSheet from '../components/BottomSheet';
import { useFeedback } from '../FeedbackContext';
import {
  createAccountInviteLink,
  listFamilyParticipants,
  removeAccountMember,
  updateAccountMember,
} from '../services/accountService';
import { remoteOperationMessage } from '../services/connectivityService';
import {
  getAccountRoleLabel,
  getAccountTypeLabel,
  getMembershipTypeLabel,
  getRelationLabel,
} from '../utils/accountPresentation';
import './FamilyMembers.css';

const RELATION_OPTIONS = [
  ['spouse', 'Cônjuge'],
  ['child', 'Filho/Filha'],
  ['parent', 'Pai/Mãe ou responsável'],
  ['other', 'Outro'],
];

const ROLE_OPTIONS = [
  ['admin', 'Administrador'],
  ['editor', 'Pode editar'],
  ['viewer', 'Somente leitura'],
];

const getMemberName = (member, currentUser) => {
  if (member.uid === currentUser?.uid) return member.displayName || currentUser.displayName || 'Membro';
  return member.displayName || member.name || 'Membro';
};

const FamilyMembers = ({ currentAccountId, currentAccount, currentRole, currentUser }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useFeedback();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [relation, setRelation] = useState('other');
  const [role, setRole] = useState('viewer');
  const [saving, setSaving] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteDisplayName, setInviteDisplayName] = useState('');

  const canManage = currentRole === 'owner' || currentRole === 'admin';
  const isFamily = currentAccount?.type === 'family';

  const loadMembers = useCallback(async () => {
    if (!currentAccountId || !isFamily) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setMembers(await listFamilyParticipants(currentAccountId));
    } catch (error) {
      showError('Não foi possível carregar os membros desta conta.', 'Família indisponível');
    } finally {
      setLoading(false);
    }
  }, [currentAccountId, isFamily, showError]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    const refreshWhenVisible = () => { if (document.visibilityState === 'visible') void loadMembers(); };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => document.removeEventListener('visibilitychange', refreshWhenVisible);
  }, [loadMembers]);

  const sortedMembers = useMemo(() => [...members].sort((a, b) => {
    if (a.role === 'owner') return -1;
    if (b.role === 'owner') return 1;
    return getMemberName(a, currentUser).localeCompare(getMemberName(b, currentUser), 'pt-BR');
  }), [members, currentUser]);

  const openInvite = () => {
    setRelation('other');
    setRole('viewer');
    setInviteLink('');
    setInviteDisplayName('');
    setInviteOpen(true);
  };

  const openEdit = (member) => {
    setRelation(member.relation || 'other');
    setRole(member.role);
    setEditingMember(member);
  };

  const handleInvite = async () => {
    setSaving(true);
    try {
      const link = await createAccountInviteLink({
        accountId: currentAccountId,
        createdBy: currentUser.uid,
        relation,
        role,
        membershipType: 'permanent',
        displayName: inviteDisplayName,
      });
      setInviteLink(link);
      await navigator.clipboard?.writeText(link);
      showSuccess('O link foi criado e copiado. Ele pode expirar antes do aceite.', 'Convite familiar pronto');
    } catch (error) {
      showError(remoteOperationMessage(error, 'Não foi possível criar o convite agora.'), 'Convite não criado');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMember = async () => {
    setSaving(true);
    try {
      await updateAccountMember({ accountId: currentAccountId, memberUid: editingMember.uid, role, relation });
      await loadMembers();
      setEditingMember(null);
      showSuccess('Relação e permissão atualizadas.', 'Membro atualizado');
    } catch (error) {
      showError(remoteOperationMessage(error, 'Você não tem permissão para aplicar essa alteração.'), 'Membro não atualizado');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!window.confirm('Remover esta pessoa da conta familiar?')) return;
    setSaving(true);
    try {
      await removeAccountMember({ accountId: currentAccountId, memberUid: editingMember.uid });
      await loadMembers();
      setEditingMember(null);
      showSuccess('A pessoa não tem mais acesso a esta conta.', 'Membro removido');
    } catch (error) {
      showError(remoteOperationMessage(error, 'Não foi possível remover este membro. O proprietário da conta não pode ser removido.'), 'Membro não removido');
    } finally {
      setSaving(false);
    }
  };

  const editorFooter = editingMember && (
    <>
      <button type="button" className="nf-sheet__primary" disabled={saving} onClick={handleSaveMember}>
        {saving ? 'Salvando...' : 'Salvar alterações'}
      </button>
      <button type="button" className="nf-sheet__danger" disabled={saving} onClick={handleRemoveMember}>
        Remover da conta
      </button>
    </>
  );

  return (
    <div className="account-page family-members-page">
      <AccountPageHeader title="Família e membros" onBack={() => navigate(-1)} />
      <main className="account-content family-members">
        <section className="family-members__summary">
          <span className="family-members__summary-icon" aria-hidden="true"><PiUsersThree size={26} /></span>
          <div>
            <h1>{currentAccount?.name || 'Conta familiar'}</h1>
            <p>{getAccountTypeLabel(currentAccount, currentUser?.uid)}</p>
          </div>
          {canManage && isFamily && (
            <button type="button" className="family-members__invite" onClick={openInvite}>
              <PiPlus size={19} aria-hidden="true" /> Convidar pessoa
            </button>
          )}
        </section>

        {!isFamily ? (
          <section className="family-members__empty">
            <h2>Esta não é uma conta familiar</h2>
            <p>Abra uma conta do tipo família para gerenciar relações familiares.</p>
          </section>
        ) : (
          <section className="family-members__section" aria-labelledby="family-members-title">
            <div className="family-members__section-heading">
              <div>
                <h2 id="family-members-title">Membros</h2>
                <p>Relação descreve a família; permissão determina o acesso financeiro.</p>
              </div>
              <span>{members.length} {members.length === 1 ? 'pessoa' : 'pessoas'}</span>
            </div>

            {loading ? (
              <p className="family-members__status">Carregando membros...</p>
            ) : sortedMembers.length === 0 ? (
              <p className="family-members__status">Nenhum membro encontrado.</p>
            ) : (
              <ul className="family-members__list">
                {sortedMembers.map((member) => {
                  const isOwner = member.role === 'owner';
                  const details = [getRelationLabel(member.relation), getAccountRoleLabel(member.role)];
                  const membershipLabel = getMembershipTypeLabel(member.membershipType);
                  return (
                    <li key={member.key || member.uid} className="family-member">
                      <span className="family-member__avatar" aria-hidden="true">
                        {getMemberName(member, currentUser).charAt(0).toUpperCase()}
                      </span>
                      <span className="family-member__content">
                        <strong>{getMemberName(member, currentUser)} {member.uid === currentUser?.uid && <small>(você)</small>}</strong>
                        <span>{details.join(' · ')}</span>
                        {membershipLabel && <small>{membershipLabel}</small>}
                      </span>
                      {member.pending ? (
                        <span className="family-member__owner">Convite pendente</span>
                      ) : isOwner ? (
                        <span className="family-member__owner"><PiCrownSimple size={17} /> Proprietário</span>
                      ) : canManage && member.linked !== false ? (
                        <button type="button" className="family-member__edit" onClick={() => openEdit(member)} aria-label={`Editar ${getMemberName(member, currentUser)}`}>
                          <PiPencilSimple size={19} aria-hidden="true" />
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </main>

      {inviteOpen && (
        <BottomSheet
          title="Convidar para a família"
          subtitle="Defina a relação e, separadamente, o que essa pessoa pode fazer."
          onClose={() => setInviteOpen(false)}
          footer={<button type="button" className="nf-sheet__primary" disabled={saving} onClick={handleInvite}>{saving ? 'Criando...' : inviteLink ? 'Gerar novo link' : 'Criar convite'}</button>}
        >
          <label className="nf-field"><span>Nome da pessoa</span><input value={inviteDisplayName} onChange={(event) => setInviteDisplayName(event.target.value)} maxLength={120} placeholder="Ex.: Rafaela" /></label>
          <MemberFields relation={relation} role={role} onRelation={setRelation} onRole={setRole} />
          {inviteLink && <label className="family-members__link">Link do convite<input readOnly value={inviteLink} onFocus={(event) => event.target.select()} /></label>}
        </BottomSheet>
      )}

      {editingMember && (
        <BottomSheet
          title={`Editar ${getMemberName(editingMember, currentUser)}`}
          subtitle="Relação é apenas uma informação familiar e não concede acesso."
          onClose={() => setEditingMember(null)}
          footer={editorFooter}
        >
          <MemberFields relation={relation} role={role} onRelation={setRelation} onRole={setRole} />
        </BottomSheet>
      )}
    </div>
  );
};

const MemberFields = ({ relation, role, onRelation, onRole }) => (
  <div className="family-member-fields">
    <label>
      <span>Relação</span>
      <select value={relation} onChange={(event) => onRelation(event.target.value)}>
        {RELATION_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
      </select>
    </label>
    <label>
      <span>Permissão</span>
      <select value={role} onChange={(event) => onRole(event.target.value)}>
        {ROLE_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
      </select>
    </label>
    <p><strong>Importante:</strong> relação familiar não altera permissões. Proprietário não pode ser escolhido por convite.</p>
  </div>
);

export default FamilyMembers;
